import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Edit, MapPin, Clock, Car, CheckCircle2, AlertCircle, Loader2, DollarSign, User, Mail, Phone, Calendar, Save, ArrowLeft, Link, Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminEditRide() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [ride, setRide] = useState(null);
  const [paymentToken, setPaymentToken] = useState(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    pickup_location: '',
    dropoff_location: '',
    date: '',
    time: '',
    miles_driven: '',
    time_hours: '',
    earnings: '',
    tips: '',
    notes: '',
    status: ''
  });

  useEffect(() => {
    fetchRide();
  }, [id]);

  const fetchRide = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/rides/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setRide(data);
        
        // Parse date and time
        const rideDate = new Date(data.date);
        const dateStr = rideDate.toISOString().split('T')[0];
        const timeStr = rideDate.toTimeString().slice(0, 5);

        setFormData({
          client_name: data.client_name || '',
          client_email: data.client_email || '',
          client_phone: data.client_phone || '',
          pickup_location: data.pickup_location || '',
          dropoff_location: data.dropoff_location || '',
          date: dateStr,
          time: timeStr,
          miles_driven: data.miles_driven?.toString() || '',
          time_hours: data.time_hours?.toString() || '',
          earnings: data.earnings?.toString() || '',
          tips: data.tips?.toString() || '0',
          notes: data.notes || '',
          status: data.status || ''
        });

        // Also fetch payment token if exists
        fetchPaymentToken(data.id);
      } else {
        setError('Failed to load ride details');
      }
    } catch (err) {
      console.error('Error fetching ride:', err);
      setError('Failed to load ride');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentToken = async (rideId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/ride/${rideId}/payment-token`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentToken(data);
      }
    } catch (err) {
      console.error('Error fetching payment token:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const recalculatePrice = async () => {
    if (!formData.miles_driven || !formData.time_hours) {
      toast.error('Please enter miles and time first');
      return;
    }

    setRecalculating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/calculate-price`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          distance_miles: parseFloat(formData.miles_driven),
          time_hours: parseFloat(formData.time_hours)
        })
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          earnings: data.final_price.toFixed(2)
        }));
        toast.success(`Price recalculated: $${data.final_price.toFixed(2)}`);
      }
    } catch (err) {
      toast.error('Failed to recalculate price');
    } finally {
      setRecalculating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Combine date and time
      const dateTime = new Date(`${formData.date}T${formData.time}`);
      
      const updateData = {
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_phone: formData.client_phone,
        pickup_location: formData.pickup_location,
        dropoff_location: formData.dropoff_location,
        date: dateTime.toISOString(),
        miles_driven: parseFloat(formData.miles_driven),
        time_hours: parseFloat(formData.time_hours),
        earnings: parseFloat(formData.earnings),
        tips: parseFloat(formData.tips) || 0,
        notes: formData.notes,
        status: formData.status
      };

      const response = await fetch(`${API_URL}/api/admin/rides/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        toast.success('Ride updated successfully!');
        
        // Also update payment token amount if it exists and price changed
        if (paymentToken && parseFloat(formData.earnings) !== ride.earnings) {
          await updatePaymentToken(parseFloat(formData.earnings));
        }
        
        // Refresh the data
        fetchRide();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to update ride');
        toast.error(data.detail || 'Failed to update ride');
      }
    } catch (err) {
      console.error('Error updating ride:', err);
      setError('Failed to update ride');
      toast.error('Failed to update ride');
    } finally {
      setSaving(false);
    }
  };

  const updatePaymentToken = async (newAmount) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/admin/payment-token/${paymentToken.token}/update-amount`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ amount: newAmount })
      });
      toast.success('Payment link amount updated!');
    } catch (err) {
      console.error('Error updating payment token:', err);
    }
  };

  const resendPaymentLink = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/rides/${id}/resend-payment-link`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Payment link resent to client!');
      } else {
        toast.error('Failed to resend payment link');
      }
    } catch (err) {
      toast.error('Failed to resend payment link');
    }
  };

  const copyPaymentLink = () => {
    if (paymentToken?.token) {
      const link = `${window.location.origin}/pay/${paymentToken.token}`;
      navigator.clipboard.writeText(link);
      toast.success('Payment link copied!');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2">Loading ride details...</span>
      </div>
    );
  }

  if (error && !ride) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-700">{error}</p>
            <Button onClick={() => navigate(-1)} className="mt-4">
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Edit className="h-6 w-6" />
              Edit Ride
            </h1>
            <p className="text-gray-500">Update ride details and invoice</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            ride?.status === 'completed' ? 'bg-green-100 text-green-800' :
            ride?.status === 'pending_payment' ? 'bg-gold-100 text-gold-800' :
            ride?.status === 'requested' ? 'bg-blue-100 text-blue-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {ride?.status?.replace('_', ' ').toUpperCase()}
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4" />
                Client Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client_name">Client Name *</Label>
                  <Input
                    id="client_name"
                    name="client_name"
                    value={formData.client_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_email">Client Email *</Label>
                  <Input
                    id="client_email"
                    name="client_email"
                    type="email"
                    value={formData.client_email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_phone">Client Phone</Label>
                <Input
                  id="client_phone"
                  name="client_phone"
                  value={formData.client_phone}
                  onChange={handleInputChange}
                />
              </div>
            </CardContent>
          </Card>

          {/* Trip Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Trip Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pickup_location">Pickup Location *</Label>
                <Input
                  id="pickup_location"
                  name="pickup_location"
                  value={formData.pickup_location}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dropoff_location">Dropoff Location *</Label>
                <Input
                  id="dropoff_location"
                  name="dropoff_location"
                  value={formData.dropoff_location}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time">Time *</Label>
                  <Input
                    id="time"
                    name="time"
                    type="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Pricing
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="miles_driven">Miles Driven *</Label>
                  <Input
                    id="miles_driven"
                    name="miles_driven"
                    type="number"
                    step="0.1"
                    value={formData.miles_driven}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time_hours">Time (hours) *</Label>
                  <Input
                    id="time_hours"
                    name="time_hours"
                    type="number"
                    step="0.25"
                    value={formData.time_hours}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="flex items-end gap-4">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="earnings">Ride Fare ($) *</Label>
                  <Input
                    id="earnings"
                    name="earnings"
                    type="number"
                    step="0.01"
                    value={formData.earnings}
                    onChange={handleInputChange}
                    required
                    className="text-lg font-bold"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={recalculatePrice}
                  disabled={recalculating}
                >
                  {recalculating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Recalculate
                </Button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tips">Tips ($)</Label>
                <Input
                  id="tips"
                  name="tips"
                  type="number"
                  step="0.01"
                  value={formData.tips}
                  onChange={handleInputChange}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex justify-between text-sm">
                  <span>Ride Fare:</span>
                  <span>{formatCurrency(formData.earnings)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Tips:</span>
                  <span>{formatCurrency(formData.tips)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t border-blue-200 pt-2 mt-2">
                  <span>Total:</span>
                  <span>{formatCurrency(parseFloat(formData.earnings || 0) + parseFloat(formData.tips || 0))}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={4}
                placeholder="Any special instructions or notes..."
              />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Status</CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending_payment">Pending Payment</SelectItem>
                  <SelectItem value="requested">Requested</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Payment Link */}
          {paymentToken && (
            <Card className="border-purple-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Link className="h-4 w-4 text-purple-600" />
                  Payment Link
                </CardTitle>
                <CardDescription>
                  {paymentToken.is_used ? (
                    <span className="text-green-600">Payment Completed</span>
                  ) : (
                    <span className="text-gold-600">Awaiting Payment</span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm bg-gray-50 p-2 rounded break-all">
                  {`${window.location.origin}/pay/${paymentToken.token}`}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={copyPaymentLink}
                    className="flex-1"
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={resendPaymentLink}
                    className="flex-1"
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    Resend
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Amount: {formatCurrency(paymentToken.amount)}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Save Button */}
          <Card>
            <CardContent className="pt-6">
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
              {error && (
                <p className="text-sm text-red-600 mt-2">{error}</p>
              )}
            </CardContent>
          </Card>

          {/* Original Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Original Info</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Created:</span>
                <span>{new Date(ride?.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Payment Status:</span>
                <span className={
                  ride?.payment_status === 'paid' ? 'text-green-600' :
                  ride?.payment_status === 'zelle_pending' ? 'text-gold-600' :
                  'text-gray-600'
                }>
                  {ride?.payment_status?.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              {ride?.payment_method && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment Method:</span>
                  <span>{ride.payment_method}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
}
